/**
 * Educational content for Image Compressor page
 */

export const ImageCompressorEducational = () => {
  return (
    <div className="space-y-8 mb-8">
      {/* Introduction Section */}
      <section className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
        <h2 className="text-2xl font-bold text-white mb-4">
          Reduce Image File Sizes Without Losing Quality
        </h2>
        <p className="text-slate-300 mb-4">
          Compress PNG, JPG, and WebP images to reduce file sizes by 40-80% while maintaining visual quality. Our 
          intelligent compression algorithm analyzes each image and applies optimal compression techniques—removing 
          unnecessary metadata, optimizing color palettes, and using efficient encoding—all without noticeable quality loss.
        </p>
        <p className="text-slate-300">
          Page load speed directly impacts user experience, SEO rankings, and conversion rates. Studies show that 
          a 1-second delay in page load time can reduce conversions by 7%. Compressing images is one of the most 
          effective ways to improve website performance, especially for image-heavy sites.
        </p>
      </section>

      {/* Why Compress Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Why Image Compression Matters
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">⚡ Faster Page Loading</h3>
            <p className="text-slate-300 text-sm">
              Smaller image files load significantly faster, especially on mobile networks. Visitors won't wait—40% of 
              users abandon sites that take more than 3 seconds to load. Compressed images keep your site snappy.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">📊 Better SEO Rankings</h3>
            <p className="text-slate-300 text-sm">
              Google considers page speed as a ranking factor. Faster sites rank higher in search results. Core Web 
              Vitals, Google's performance metrics, heavily reward optimized images with better positions.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">💸 Reduced Bandwidth Costs</h3>
            <p className="text-slate-300 text-sm">
              Lower file sizes mean less data transfer. For high-traffic websites, this translates to significant 
              savings on hosting and CDN costs. Mobile users also benefit from reduced data usage on metered connections.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-indigo-400 mb-2">📱 Mobile Performance</h3>
            <p className="text-slate-300 text-sm">
              Mobile devices often have slower connections than desktop. Compressed images ensure your site performs 
              well on 3G/4G networks, providing a smooth experience for mobile users who make up 60%+ of web traffic.
            </p>
          </div>
        </div>
      </section>

      {/* Compression Types Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Understanding Compression: Lossy vs Lossless
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-green-400 mb-3">Lossless Compression</h3>
            <p className="text-slate-300 text-sm mb-3">
              Reduces file size without any quality loss. The compressed image is pixel-for-pixel identical to the 
              original. This works by removing unnecessary metadata and using efficient encoding.
            </p>
            <div className="space-y-2 text-sm">
              <p className="text-slate-300"><strong className="text-white">Best for:</strong> Screenshots, diagrams, logos, graphics with text</p>
              <p className="text-slate-300"><strong className="text-white">Size reduction:</strong> Typically 10-30%</p>
              <p className="text-slate-300"><strong className="text-white">Quality:</strong> 100% identical to original</p>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-5 border border-slate-700">
            <h3 className="text-lg font-medium text-blue-400 mb-3">Lossy Compression</h3>
            <p className="text-slate-300 text-sm mb-3">
              Achieves much greater size reduction by discarding some image data. Modern algorithms are smart enough 
              to remove information humans can't perceive, maintaining apparent quality.
            </p>
            <div className="space-y-2 text-sm">
              <p className="text-slate-300"><strong className="text-white">Best for:</strong> Photos, complex images, backgrounds</p>
              <p className="text-slate-300"><strong className="text-white">Size reduction:</strong> Typically 40-80%</p>
              <p className="text-slate-300"><strong className="text-white">Quality:</strong> Slight loss, usually imperceptible</p>
            </div>
          </div>
        </div>
      </section>

      {/* Format Comparison Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Choosing the Right Image Format
        </h2>
        <div className="space-y-3">
          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-blue-500">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-medium text-white">JPG/JPEG</h3>
              <span className="text-xs bg-blue-600 px-2 py-1 rounded">Lossy</span>
            </div>
            <p className="text-slate-300 text-sm mb-2">
              Best for photographs and images with many colors. Excellent compression ratios but doesn't support 
              transparency. Use quality settings 75-85% for the best balance of size and visual quality.
            </p>
            <p className="text-xs text-slate-400">
              <strong>Ideal for:</strong> Photos, complex images, backgrounds • 
              <strong> Avoid for:</strong> Logos, icons, text, images needing transparency
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-green-500">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-medium text-white">PNG</h3>
              <span className="text-xs bg-green-600 px-2 py-1 rounded">Lossless</span>
            </div>
            <p className="text-slate-300 text-sm mb-2">
              Perfect for images requiring transparency or sharp edges. Supports alpha channel for smooth transparent 
              backgrounds. Larger file sizes than JPG but maintains perfect quality. PNG-8 (256 colors) is smaller 
              than PNG-24 (millions of colors).
            </p>
            <p className="text-xs text-slate-400">
              <strong>Ideal for:</strong> Logos, icons, graphics with text, transparent images • 
              <strong> Avoid for:</strong> Large photographs (use JPG instead)
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-purple-500">
            <div className="flex justify-between items-start mb-2">
              <h3 className="font-medium text-white">WebP</h3>
              <span className="text-xs bg-purple-600 px-2 py-1 rounded">Both</span>
            </div>
            <p className="text-slate-300 text-sm mb-2">
              Modern format offering superior compression compared to both JPG and PNG. Supports both lossy and 
              lossless compression plus transparency. 25-35% smaller than JPG at equivalent quality. Supported by 
              all major browsers since 2020.
            </p>
            <p className="text-xs text-slate-400">
              <strong>Ideal for:</strong> Everything—photos, graphics, transparency • 
              <strong> Note:</strong> Legacy browser compatibility (IE) requires JPG/PNG fallbacks
            </p>
          </div>
        </div>
      </section>

      {/* Optimization Tips Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Image Optimization Best Practices
        </h2>
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <ul className="space-y-3 text-slate-300">
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Compress before uploading:</strong> Always optimize images before adding 
                them to your website. Don't rely on CMS automatic compression—it's often less effective than manual 
                optimization with quality tools.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Use appropriate dimensions:</strong> Don't upload 4000px images to display 
                at 800px. Resize images to their maximum display size first, then compress. This can reduce file size 
                by 70-90% compared to full-resolution originals.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Find the quality sweet spot:</strong> For JPG/WebP, quality settings 
                between 75-85% provide excellent visual results at significantly reduced file sizes. Going higher 
                increases size dramatically with minimal visible improvement.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Test on actual devices:</strong> View compressed images on real phones 
                and tablets, not just desktop. Small screens are more forgiving of compression, allowing more aggressive 
                settings without noticeable quality loss.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Implement lazy loading:</strong> After compression, use lazy loading 
                (loading="lazy") to defer off-screen images until needed. This improves initial page load even further.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Use CDN for image delivery:</strong> Combine compression with a CDN to 
                serve images from servers geographically close to users. This reduces latency and further improves 
                perceived performance.
              </div>
            </li>
            <li className="flex items-start">
              <span className="text-indigo-400 mr-3 mt-1">✓</span>
              <div>
                <strong className="text-white">Monitor Core Web Vitals:</strong> Use Google PageSpeed Insights and 
                Chrome DevTools to measure Largest Contentful Paint (LCP). Optimized images directly improve this 
                crucial metric for SEO and user experience.
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* Impact Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          Real-World Performance Impact
        </h2>
        <p className="text-slate-300 mb-4">
          Image optimization delivers measurable business results:
        </p>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-indigo-900/50 to-slate-800/50 rounded-lg p-5 border border-indigo-700/50">
            <div className="text-3xl font-bold text-indigo-400 mb-2">47%</div>
            <p className="text-slate-300 text-sm">
              Of users expect pages to load in 2 seconds or less. Optimized images are essential to meeting this expectation.
            </p>
          </div>

          <div className="bg-gradient-to-br from-green-900/50 to-slate-800/50 rounded-lg p-5 border border-green-700/50">
            <div className="text-3xl font-bold text-green-400 mb-2">32%</div>
            <p className="text-slate-300 text-sm">
              Increase in conversions when page load time improves from 8 to 2 seconds, according to Walmart's 
              performance study.
            </p>
          </div>

          <div className="bg-gradient-to-br from-purple-900/50 to-slate-800/50 rounded-lg p-5 border border-purple-700/50">
            <div className="text-3xl font-bold text-purple-400 mb-2">$100k</div>
            <p className="text-slate-300 text-sm">
              Amazon found that every 100ms improvement in page speed increased revenue by 1%—millions for large sites.
            </p>
          </div>
        </div>
      </section>

      {/* Technical Section */}
      <section>
        <h2 className="text-xl font-semibold text-white mb-4">
          How Our Compression Tool Works
        </h2>
        <p className="text-slate-300 mb-4">
          Our browser-based compression uses advanced algorithms to optimize images:
        </p>
        <div className="space-y-3">
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">Smart Quality Analysis</h3>
            <p className="text-slate-300 text-sm">
              The tool analyzes image complexity, color distribution, and content type to determine optimal compression 
              settings automatically. You can also manually adjust quality for precise control.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">Metadata Stripping</h3>
            <p className="text-slate-300 text-sm">
              Automatically removes EXIF data, camera settings, GPS coordinates, thumbnails, and comments embedded in 
              images. This often saves 10-20KB per image without any visual change.
            </p>
          </div>

          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="font-medium text-white mb-2">Client-Side Processing</h3>
            <p className="text-slate-300 text-sm">
              All compression happens in your browser using HTML5 Canvas API. Your images never leave your device, 
              ensuring complete privacy and security for sensitive photos or proprietary graphics.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
